import { Header } from "../components/Header";
import { Footer } from "../components/Footer";
import { Button } from "../components/ui/button";
import { ArrowRight, ArrowLeft, Cpu, Info } from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";

export default function EvaluatePage() {
  const navigate = useNavigate();
  const location = useLocation();
  const smartStrategyHome = location.pathname.startsWith("/warehousex/")
    ? "/warehousex/smartstrategy"
    : "/smartstrategy";

  return (
    <div className="min-h-screen flex flex-col bg-[#F8FAFC]">
      <Header />

      <main className="flex-1 flex items-start justify-center pt-6 sm:pt-8 pb-10">
        <div className="w-full max-w-3xl mx-auto px-4 sm:px-6">
          <div className="bg-white border border-[#E5E7EB] rounded-sm shadow-sm p-6 sm:p-8">
            {/* AI badge */}
            <div className="flex justify-center mb-4">
              <div className="inline-flex items-center gap-1.5 bg-[#E0F2FE] border border-[#025979]/15 rounded-sm px-3 py-1.5">
                <Cpu className="w-3.5 h-3.5 text-[#025979]" strokeWidth={1.5} />
                <span className="text-xs font-medium text-[#025979]">SAP Warehouse Strategy Assessment</span>
              </div>
            </div>

            {/* WarehouseX Solution eyebrow */}
            <p
              data-testid="evaluate-page-eyebrow"
              className="text-center text-xs font-medium uppercase tracking-wide text-[#9CA3AF]"
            >
              WarehouseX Solution
            </p>

            {/* Title */}
            <h1
              data-testid="evaluate-page-title"
              className="text-center text-3xl sm:text-4xl lg:text-[2.75rem] font-bold tracking-tight text-[#111827] leading-tight mt-1"
              style={{ fontFamily: 'Chivo, sans-serif' }}
            >
              SmartEvaluate
            </h1>

            {/* Tagline */}
            <p
              data-testid="evaluate-page-tagline"
              className="mt-1.5 text-center text-sm font-medium text-[#025979]"
            >
              Evaluate. Decide. Transform.
            </p>

            {/* Subtitle */}
            <p
              data-testid="evaluate-page-subtitle"
              className="mt-3 text-center text-sm sm:text-base text-[#6B7280] max-w-xl mx-auto leading-relaxed"
            >
              Assess your SAP warehouse landscape and receive expert recommendations, implementation roadmap, timeline, and business case.
            </p>

            {/* Intro context — not a live step indicator, just what the module opens with */}
            <div data-testid="evaluate-step-indicator" className="mt-4 flex items-center justify-center gap-2">
              <div className="w-5 h-5 rounded-full bg-[#025979] flex items-center justify-center">
                <span className="text-[10px] font-bold text-white">1</span>
              </div>
              <span className="text-xs font-medium text-[#111827]">
                Choose the assessment that best matches your SAP warehouse transformation journey.
              </span>
            </div>

            {/* Assessment Scenarios */}
            <div data-testid="assessment-scenarios" className="mt-4">
              <p className="text-xs font-semibold uppercase tracking-wide text-[#9CA3AF] text-center mb-2.5">
                Assessment Scenarios
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div data-testid="scenario-greenfield" className="bg-[#F8FAFC] border border-[#E5E7EB] rounded-sm p-3.5 flex flex-col">
                  <div className="flex items-center gap-1.5 mb-1.5">
                    <span className="w-2 h-2 rounded-full bg-[#16A34A]" />
                    <h3 className="text-sm font-semibold text-[#111827]">Greenfield</h3>
                  </div>
                  <p className="text-xs text-[#6B7280] leading-relaxed">
                    Planning a new SAP warehouse implementation.
                  </p>
                </div>
                <div data-testid="scenario-brownfield" className="bg-[#F8FAFC] border border-[#E5E7EB] rounded-sm p-3.5 flex flex-col">
                  <div className="flex items-center gap-1.5 mb-1.5">
                    <span className="w-2 h-2 rounded-full bg-[#EA580C]" />
                    <h3 className="text-sm font-semibold text-[#111827]">Brownfield</h3>
                  </div>
                  <p className="text-xs text-[#6B7280] leading-relaxed">
                    Modernizing an existing SAP WM, Stock Room Management or SAP EWM landscape.
                  </p>
                </div>
                <div data-testid="scenario-cloud-migration" className="bg-[#F8FAFC] border border-[#E5E7EB] rounded-sm p-3.5 flex flex-col">
                  <div className="flex items-center gap-1.5 mb-1.5">
                    <span className="w-2 h-2 rounded-full bg-[#2563EB]" />
                    <h3 className="text-sm font-semibold text-[#111827]">Cloud Migration</h3>
                  </div>
                  <p className="text-xs text-[#6B7280] leading-relaxed">
                    Planning migration from on-premise SAP warehouse systems to SAP S/4HANA Private Cloud.
                  </p>
                </div>
              </div>
            </div>

            {/* Divider */}
            <div className="mt-5 mb-5 border-t border-[#E5E7EB]"></div>

            {/* CTA buttons */}
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <Button
                data-testid="evaluate-start-btn"
                onClick={() => {
                  // Starting a NEW evaluation must not rehydrate a previous
                  // draft — clear the draft keys so Steps 1-3 begin empty.
                  localStorage.removeItem("warehousex_assessment_id");
                  localStorage.removeItem("warehousex_email");
                  navigate("/evaluate/module-1");
                }}
                className="w-full sm:flex-1 bg-[#025979] hover:bg-[#03738C] text-white py-2.5 text-sm font-medium rounded-sm transition-colors shadow-sm"
              >
                Start Evaluation
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
              <Button
                data-testid="evaluate-resume-btn"
                variant="outline"
                onClick={() => navigate("/evaluate/resume")}
                className="w-full sm:flex-1 py-2.5 rounded-sm text-sm border-[#025979] text-[#025979] hover:bg-[#E0F2FE] hover:text-[#025979] hover:border-[#025979] transition-colors"
              >
                Resume Evaluation
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </div>

            <div className="mt-3 flex justify-center">
              <button
                data-testid="evaluate-back-btn"
                onClick={() => navigate(smartStrategyHome)}
                className="text-xs text-[#6B7280] hover:text-[#025979] flex items-center gap-1.5 transition-colors"
              >
                <ArrowLeft className="w-3.5 h-3.5" />
                Back to SmartStrategy
              </button>
            </div>

            {/* AI guidance info */}
            <div data-testid="evaluate-ai-hint" className="mt-4 flex items-start gap-2 bg-[#F8FAFC] border border-[#E5E7EB] rounded-sm px-3.5 py-2.5">
              <Info className="w-3.5 h-3.5 text-[#025979] mt-0.5 shrink-0" strokeWidth={1.5} />
              <span className="text-xs text-[#6B7280] leading-relaxed">
                SmartEvaluate combines SAP best practices with consultant expertise to recommend the right transformation strategy for your business.
              </span>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
